import { Hidden } from '@ahmdigital/ui';
import { map } from 'lodash/fp';
import { useFormikContext } from 'formik';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import { FormValues, SetIsWidgetActive, ToggleModal } from '../../types';
import { FormWrapper, StyledButton, StyledForm } from '../../styles';
import { InputSeparator } from '../step-input/styles';
import { SESSION_STORAGE_KEYS } from '../../../../constants';
import browserUtil from '../../../../../../../../utils/browser';
import fieldData from '../../data';
import getFormValuesFromUrl from '../../utils/get-form-values-from-url';
import getShouldHideSeparator from '../../utils/get-should-hide-separator';
import rehydrateForm from '../../utils/rehydrate-form';
import StepInput, { StepInputProps } from '../step-input';
import uncapFpIterator from '../../../../../../../../../utils/uncap-fp-iterator';
import useOpenPanelOnError from '../../hooks/use-open-panel-on-error';
import useTravelWidgetContext from '../../hooks/use-travel-widget-context';

const mapWithKey = uncapFpIterator(map);

type WidgetFormProps = {
  handleCloseWidgetOnTab: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  modalStates: boolean[];
  toggleModal: ToggleModal;
  setIsWidgetActive: SetIsWidgetActive;
};

const WidgetForm = ({ handleCloseWidgetOnTab, modalStates, toggleModal, setIsWidgetActive }: WidgetFormProps) => {
  const windowObj = browserUtil.getWindow();
  const { countries, travellerCount, setTravellerCount, setCurrentWidgetErrors, currentWidgetErrors } =
    useTravelWidgetContext();
  const { initialValues, handleSubmit, isSubmitting, resetForm, setSubmitting, values, errors, validateForm } =
    useFormikContext<FormValues>();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hasFormInitialized, setHasFormInitialized] = useState(false);

  useOpenPanelOnError({ setIsWidgetActive, toggleModal });
  const router = useRouter();
  const destinationLocation = router.query.productOptionValue as string | undefined;
  const formikSessionStorageKey = `${SESSION_STORAGE_KEYS.FORMIK_STATE}${destinationLocation ? `-${destinationLocation}` : ''}`;
  const travellerCountSessionStorageKey = `${SESSION_STORAGE_KEYS.TRAVELLER_COUNT}${destinationLocation ? `-${destinationLocation}` : ''}`;

  const valuesFromUrl = getFormValuesFromUrl({
    countries,
    destinationLocation,
    initialValues,
    queryStringValues: router.query,
  });

  const rehydrateFormParams = {
    countries,
    formikSessionStorageKey,
    hasFormInitialized,
    resetForm,
    setHasFormInitialized,
    setSubmitting,
    setTravellerCount,
    travellerCountSessionStorageKey,
    valuesFromUrl,
  };

  useEffect(() => {
    setCurrentWidgetErrors(errors);
  }, [errors, setCurrentWidgetErrors]);

  useEffect(() => {
    validateForm();
  }, [currentWidgetErrors]);

  useEffect(() => {
    const handlePageShow = (event: Event) => {
      const pageTransitionEvent = event as PageTransitionEvent;
      // indicates the page has loaded from a cache (specifically the safari back/forward cache)
      if (pageTransitionEvent.persisted) {
        rehydrateForm({ ...rehydrateFormParams, hasPersistedEventRun: true });
      }
    };
    rehydrateForm({ ...rehydrateFormParams });

    windowObj.addEventListener('pageshow', handlePageShow);

    return () => {
      windowObj.removeEventListener('pageshow', handlePageShow);
    };
  }, [countries, valuesFromUrl]);

  useEffect(() => {
    // we only want to start storing the values when we have checked if we need to rehydrate the form
    if (hasFormInitialized) {
      windowObj.sessionStorage.setItem(formikSessionStorageKey, JSON.stringify(values));
      windowObj.sessionStorage.setItem(travellerCountSessionStorageKey, travellerCount.toString());
    }
  }, [values, travellerCount, countries, hasFormInitialized]);

  return (
    <StyledForm>
      <FormWrapper>
        {mapWithKey((field: StepInputProps, index: number) => {
          const isFirst = index === 0;
          const isLast = index === fieldData.length - 1;

          const isActive = modalStates[index] === true;
          const prevPanelHideSeparator = modalStates[index + 1] === true;

          const showInputSeparator = !(
            getShouldHideSeparator(hoveredIndex, index) ||
            isActive ||
            prevPanelHideSeparator
          );

          return (
            <>
              <StepInput
                key={index}
                {...field}
                isFirst={isFirst}
                isLast={isLast}
                isModalOpen={modalStates[index]}
                setIsModalOpen={(value) => {
                  toggleModal(index, value);
                  if (value) {
                    setIsWidgetActive(true);
                  }
                }}
                index={index}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                setIsWidgetActive={setIsWidgetActive}
                handleCloseWidgetOnTab={handleCloseWidgetOnTab}
              />
              {!isLast && <InputSeparator isVisible={showInputSeparator} className="input-separator" />}
            </>
          );
        }, fieldData)}
      </FormWrapper>
      <Hidden df xl lg>
        <div>
          <StyledButton
            icon={{
              name: 'arrow-right-long',
              position: 'right',
              size: 'large',
            }}
            onClick={handleSubmit}
            type="submit"
            variant="flair"
            isLoading={isSubmitting}
          >
            Get quote
          </StyledButton>
        </div>
      </Hidden>
    </StyledForm>
  );
};

export default WidgetForm;
